import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ActivityLog } from 'src/app/modules/admin/campaign-management/campaign-tracking.model';
import { UserModel } from 'src/app/modules/admin/user-management/user-model';
import { UserService } from 'src/app/modules/admin/user-management/user.service';

interface ParsedLogInfo {
  [key: string]: string;
}

interface ActivityMeta {
  type: 'Call' | 'Email' | 'CFCall' | 'Other';
  action: string | null;
  // common
  description: string | null;
  time?: Date | string | null;
  // email-only
  title?: string | null;
  sender?: string | null;
  senderDisplay?: string | null;
  recipient?: string | null;
  body?: string | null;
  // call-only
  caller?: string | null;
  phone?: string | null;
  event?: string | null;
  duration?: string | null;
  result?: string | null;
  // CFCall specific fields
  user?: string | null;
  callEvent?: string | null;
}

@Component({
  selector: 'app-external-log-detail',
  templateUrl: './external-log-detail.component.html',
  styleUrls: ['./external-log-detail.component.scss']
})
export class ExternalLogDetailComponent implements OnInit {

  activityLog: ActivityLog;
  logInfo1Parsed: ParsedLogInfo = {};
  logInfo2Parsed: ParsedLogInfo = {};
  user: UserModel = null;
  activityMeta: ActivityMeta | null = null;
  userList: UserModel[] = [];
  constructor(
    public dialModalRef: MatDialogRef<ExternalLogDetailComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public userService: UserService
  ) {
    this.activityLog = data.activityLog ?? this.activityLog;
    this.userService.allUserList().subscribe(res => {
      this.userList = res;
    });
  }

  ngOnInit() {
    this.parseLogInfo();
    // Build unified meta for template
    if (this.activityLog?.subObjName.toLowerCase() == 'externalemail') {
      const senderUsername = this.logInfo2Parsed['emailreceived'] || this.logInfo2Parsed['email'] || null;
      
      this.activityMeta = {
        type: 'Email',
        action: this.activityLog?.action || null,
        title: this.logInfo1Parsed['title'] || null,
        sender: senderUsername,
        senderDisplay: senderUsername, // Will be updated when user data loads
        recipient: this.logInfo1Parsed['emailsend'] || null,
        description: this.logInfo2Parsed['description'] || null,
        body: this.logInfo2Parsed['body'] || null,
        time: this.activityLog?.dateCreated || null,
      };
      
      // Resolve sender display name after activityMeta is created
      if (senderUsername && senderUsername !== 'unknown') {
        this.getUserByUserName(senderUsername);
      }
    } else if (this.activityLog?.subObjName.toLowerCase() == 'externalcall') {
      this.activityMeta = {
        type: 'Call',
        action: this.activityLog?.action || null,
        caller: this.logInfo1Parsed['userCall'] || null,
        phone: this.activityLog?.subObjId || null,
        duration: this.logInfo1Parsed['duration'] || null,
        description: this.logInfo2Parsed['description'] || null,
        time: this.activityLog?.dateCreated || null,
      };
    }else if(this.activityLog?.activityType.toLowerCase() == 'cfcall' && this.activityLog?.logInfo1 && this.activityLog?.logInfo1.toLowerCase().includes('summarytext') && !this.activityLog?.logInfo2){
      // Parse JSON from logInfo1 for CFCall
      let cfCallData = {};
      try {
        cfCallData = JSON.parse(this.activityLog?.logInfo1 || '{}');  
      } catch (e) {
        console.warn('Error parsing CFCall logInfo1:', e);
      }
      
      this.activityMeta = {
        type: 'CFCall',
        action: this.activityLog?.action || null,
        user: cfCallData['userId'] || null,
        caller: cfCallData['outboundCallerId'] || null,
        phone: cfCallData['outboundPhoneNumber'] || null,
        duration: cfCallData['duration'] ? `${cfCallData['duration']}s` : null,
        description: cfCallData['SummaryText'] || cfCallData['summaryText'] || null,
        time: cfCallData['startCallDatetime'] || null,
        callEvent: cfCallData['callEvent'] || null,
      };
    }else{
      this.activityMeta = {
        type: 'Other',
        action: this.activityLog?.logInfo1 || null,
        description: this.activityLog?.logInfo2 || null,
      };
    }
  }

  parseLogInfo() {
    if (this.activityLog?.logInfo1) {
      this.logInfo1Parsed = this.parseSemicolonData(this.activityLog.logInfo1);
    }
    if (this.activityLog?.logInfo2) {
      this.logInfo2Parsed = this.parseSemicolonData(this.activityLog.logInfo2);
    }
  }

  parseSemicolonData(data: string): ParsedLogInfo {
    const result: ParsedLogInfo = {};
    
    if (!data) {
      return result;
    }

    // Check if data has curly braces (JSON-like format in LogInfo2)
    const hasJsonFormat = data.trim().startsWith('{');
    
    // Remove curly braces if present
    let cleanData = data.replace(/^\{|\}$/g, '').trim();

    // Only normalize JSON format if it has curly braces (LogInfo2)
    if (hasJsonFormat) {
      // Normalize JSON-like quoted key:value pairs to semicolon pairs ("key":"value" -> key;value)
      cleanData = cleanData.replace(/"([^"]+)"\s*:\s*"([^"]*)"/g, '$1;$2');
    }
    
    // Split by semicolon
    const parts = cleanData.split(';');
    
    // Process pairs
    for (let i = 0; i < parts.length - 1; i += 2) {
      const key = parts[i]?.trim();
      const value = parts[i + 1]?.trim().replace(/^"|"$/g, ''); // Remove quotes
      if (key) {
        result[key] = value || '';
      }
    }
    
    return result;
  }

  closeDialog() {
    this.dialModalRef.close();
  }

  getUserByUserName(userName: string) {
    this.user = this.userList.find(user => user.userName === userName);
    // Update activityMeta with resolved user display name
    if (this.activityMeta && this.activityMeta.type === 'Email') {
      this.activityMeta.senderDisplay = this.user?.email || userName;
    }
  }

  // Return a generic badge class based on common keywords to avoid hard-coding exact statuses
  getBadgeClass(value: string): string {
    if (!value) return 'badge-info';
    const s = value.toLowerCase();
    if (['done', 'success', 'completed', 'answered', 'ok', 'sent'].some(k => s.includes(k))) return 'badge-success';
    if (['fail', 'failed', 'error', 'bounce'].some(k => s.includes(k))) return 'badge-danger';
    if (['pending', 'processing', 'busy', 'no_answer', 'deferred', 'voicemail', 'waiting'].some(k => s.includes(k))) return 'badge-warning';
    return 'badge-info';
  }
}
